import type { CSSProperties } from 'react';
import './Scoreboard.css';

export interface ScoreboardStat {
  value: string;
  label: string;
}

interface ScoreboardProps {
  label: string;
  meta: string;
  stats: readonly ScoreboardStat[];
}

/**
 * Split-flap scoreboard.
 *
 * Three stacked layers per panel:
 *
 *   1. the resting layer, showing the final value — always present, and the
 *      only one a screen reader sees;
 *   2. the top flap, showing "––", which rotates away and then hard-cuts to
 *      transparent (flapFade with step-end, so it disappears at the moment the
 *      rotation hides it rather than ghosting through);
 *   3. the bottom flap, showing the final value, which swings up into place.
 *
 * Layers 2 and 3 are aria-hidden: they are the same number twice more, and a
 * scoreboard that reads "10th, dash dash, 10th" is worse than one that reads
 * "10th". With prefers-reduced-motion the animations collapse to their end
 * state — flap gone, value showing — so nothing is left mid-flip.
 *
 * Stagger is passed as custom properties rather than composed animation strings
 * so the timing functions and durations stay in the stylesheet.
 */
export default function Scoreboard({ label, meta, stats }: ScoreboardProps) {
  return (
    <section className="scoreboard invert">
      <div className="wrap wrap--wide">
        <p className="eyebrow eyebrow--rule scoreboard__head">
          <span>{label}</span>
          <span className="scoreboard__meta">{meta}</span>
        </p>

        <div className="scoreboard__grid">
          {stats.map((stat, i) => (
            <div
              key={stat.label}
              className="flap"
              style={
                {
                  '--flap-drop': `${i * 0.1}s`,
                  '--flap-top': `${0.5 + i * 0.12}s`,
                  '--flap-bottom': `${0.98 + i * 0.12}s`,
                } as CSSProperties
              }
            >
              <div className="flap__window">
                <div className="flap__rest">
                  <span className="flap__digit">{stat.value}</span>
                </div>

                <div className="flap__top" aria-hidden="true">
                  <div className="flap__half">
                    <span className="flap__digit flap__digit--blank">
                      &ndash;&ndash;
                    </span>
                  </div>
                </div>

                <div className="flap__bottom" aria-hidden="true">
                  <div className="flap__half flap__half--lower">
                    <span className="flap__digit">{stat.value}</span>
                  </div>
                </div>

                <div className="flap__hinge" aria-hidden="true" />
              </div>

              <div className="flap__label">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
